import { AlertCircle, Monitor, Tablet } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import {
	Alert,
	AlertDescription,
	AlertTitle,
} from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import {
	deviceManagementApi,
	isSelectableTerminalDevice,
	type Device,
} from "@workspace/venue-management/devices";

export function DeviceSelection({
	tenant,
	venueId,
	onSelect,
}: {
	tenant: string;
	venueId: string;
	onSelect: (deviceId: string) => void;
}) {
	const { data, isLoading } = useQuery(
		deviceManagementApi.getDevicesOptions(tenant, venueId),
	);

	const terminalDevices =
		data?.devices.filter((d: Device) => isSelectableTerminalDevice(d)) ?? [];

	if (!isLoading && terminalDevices.length === 0) {
		return (
			<div className="flex h-[var(--kiosk-vh,100vh)] items-center justify-center p-4">
				<Alert className="w-full max-w-xl" variant="destructive" role="alert">
					<AlertCircle />
					<AlertTitle className="scroll-m-20 text-2xl font-semibold tracking-tight">
						No terminal devices registered
					</AlertTitle>
					<AlertDescription>
						No Till or Tablet devices are registered for this venue. Please
						contact a manager to register a device in venue settings.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<div className="flex h-[var(--kiosk-vh,100vh)] items-center justify-center p-4">
			<Alert className="w-full max-w-xl">
				<Monitor className="size-4" />
				<AlertTitle className="scroll-m-20 text-2xl font-semibold tracking-tight">
					Select Terminal Device
				</AlertTitle>
				<AlertDescription>
					Choose which device this terminal is running on. This determines print
					routing and queue suppression.
				</AlertDescription>
				<div className="col-start-2 mt-4 space-y-2">
					{isLoading && (
						<p className="text-sm text-muted-foreground">Loading devices...</p>
					)}
					{terminalDevices.map((device: Device) => (
						<Button
							key={device.id}
							variant="outline"
							size="lg"
							className="h-16 w-full justify-start gap-3"
							onClick={() => onSelect(device.id)}
						>
							{device.type === "Till" ? (
								<Monitor className="size-5 shrink-0" />
							) : (
								<Tablet className="size-5 shrink-0" />
							)}
							<div className="text-left">
								<div className="font-medium">{device.name}</div>
								<div className="text-xs text-muted-foreground">
									{device.type} — {device.identifier}
								</div>
							</div>
						</Button>
					))}
				</div>
			</Alert>
		</div>
	);
}
